// The external ban's stored vocabulary (#7785 part 3): enums and shapes only, off the audit
// payloads' import path, so `entities/audit` never loads the rules' dependencies. The rules and the
// module map are `../external-ban.ts`; import that barrel.

import { z } from 'zod';

/** What the danger judge answers. Stored, so never renamed. */
export const EXTERNAL_DANGER_VERDICTS = ['clean', 'suspect', 'dangerous'] as const;
export type ExternalDangerVerdictName = (typeof EXTERNAL_DANGER_VERDICTS)[number];

/** The mechanism the judge names. An enum on purpose: it can carry no author text. Stored. */
export const EXTERNAL_DANGER_CATEGORIES = [
  'code_execution',
  'credential_theft',
  'backdoor',
  'agent_hijack',
  'destructive',
  'malware_link',
] as const;
export const ExternalDangerCategorySchema = z.enum(EXTERNAL_DANGER_CATEGORIES);
export type ExternalDangerCategory = z.infer<typeof ExternalDangerCategorySchema>;

/** One judgement, as stored and as the core reads it. */
export const ExternalDangerVerdictSchema = z.discriminatedUnion('verdict', [
  z.object({ verdict: z.literal('clean'), category: z.null() }),
  z.object({ verdict: z.literal('suspect'), category: ExternalDangerCategorySchema }),
  z.object({ verdict: z.literal('dangerous'), category: ExternalDangerCategorySchema }),
]);
export type ExternalDangerVerdict = z.infer<typeof ExternalDangerVerdictSchema>;

/** Which read of one text a verdict answers. Stored. */
export const EXTERNAL_DANGER_READS = ['first', 'second'] as const;
export type ExternalDangerRead = (typeof EXTERNAL_DANGER_READS)[number];

/** What the judge read. Stored, so never renamed. */
export const EXTERNAL_ITEM_KINDS = ['pull_request', 'issue'] as const;
export const ExternalItemKindSchema = z.enum(EXTERNAL_ITEM_KINDS);
export type ExternalItemKind = z.infer<typeof ExternalItemKindSchema>;

/** The named profile signals. Stored in a ban's evidence, so never renamed. */
export const EXTERNAL_SCRUTINY_SIGNALS = [
  'fresh_account',
  'no_followers',
  'no_starred_repos',
  'only_pull_requests',
] as const;
export const ExternalScrutinySignalSchema = z.enum(EXTERNAL_SCRUTINY_SIGNALS);
export type ExternalScrutinySignal = z.infer<typeof ExternalScrutinySignalSchema>;

/** How an account's ban came to be. Stored. */
export const EXTERNAL_BAN_SOURCES = ['verdict', 'member'] as const;
export const ExternalBanSourceSchema = z.enum(EXTERNAL_BAN_SOURCES);
export type ExternalBanSource = z.infer<typeof ExternalBanSourceSchema>;

/** How a shared entry came to be. Stored. */
export const EXTERNAL_SHARED_BAN_SOURCES = ['corroborated', 'direct'] as const;
export const ExternalSharedBanSourceSchema = z.enum(EXTERNAL_SHARED_BAN_SOURCES);
export type ExternalSharedBanSource = z.infer<typeof ExternalSharedBanSourceSchema>;
